import dayjs from "dayjs";
import { isAdminOrEmployee, trimText } from "../../../../utils/Functions";
import { getDateObject } from "../../../../utils/DateTimeRepository";
import { useTranslation } from "react-i18next";
import React from "react";
import { Link } from "react-router-dom";
import {
  petViewUrl,
  PETS,
  SINGLE_CLINIC_USER,
} from "../../../../enums/UrlPaths";
import { Chip } from "@mui/material";
import FaceIcon from "@mui/icons-material/Face";
import ApartmentIcon from "@mui/icons-material/Apartment";
import useUser from "../../../../hooks/useUser";
import { USER } from "../../../../enums/Roles";
import { TbGrave2 } from "react-icons/tb";
const NO_INFO = "NoInfo";

//TODO: check if service translation works after new implementation
const useGetSingleAppointmentDetails = (appointement) => {
  const { roles, currentClinic } = useUser();
  const showAdminView = roles.adminOrEmployee || roles.master;
  const { t, i18n } = useTranslation();

  const currentLang = i18n.language;
  const [translations, setTranslations] = React.useState({
    species: "",
    service: "",
  });

  React.useEffect(() => {
    var currentLangName = null;
    var currentSpeciesName = null;
    if (appointement?.priceList?.translations != null) {
      currentLangName = appointement?.priceList?.translations[currentLang];
    }

    if (appointement?.pet?.type?.translations != null) {
      currentSpeciesName = appointement?.pet?.type?.translations[currentLang];
    }

    if (currentLangName != null && currentLangName != "") {
      setTranslations((prevState) => ({
        ...prevState,
        service: currentLangName,
      }));
    } else {
      setTranslations((prevState) => ({
        ...prevState,
        service: appointement?.priceList?.title,
      }));
    }

    if (currentSpeciesName != null && currentSpeciesName != "") {
      setTranslations((prevState) => ({
        ...prevState,
        species: currentSpeciesName,
      }));
    } else {
      setTranslations((prevState) => ({
        ...prevState,
        species: appointement?.pet?.type?.title,
      }));
    }
  }, [appointement, currentLang]);

  return showAdminView
    ? getAdminAppointmentDetails(
        appointement,
        translations.service,
        t,
        currentClinic,
      )
    : getUserAppointmentDetails(appointement, translations.service, t);
};

function getUserAppointmentDetails(appointement, service, t) {
  const details = [
    {
      label: "DateAndTimeOfAppointment",
      value:
        dayjs(getDateObject(appointement?.startDate)).format(
          "DD/MM/YYYY, HH:mm",
        ) || t(NO_INFO),
    },
    {
      label: "Service",
      value: service || t(NO_INFO),
    },
    {
      label: "Description",
      value: appointement?.description || t(NO_INFO),
    },
    {
      label: "Treatment",
      value: appointement?.treatment?.description || t(NO_INFO),
    },
    {
      label: "Location",
      value:
        `${appointement?.clinic?.title} - ${appointement?.clinic?.streetAddress}, ${appointement?.clinic?.city}` ||
        t(NO_INFO),
    },
  ];

  if (appointement && appointement?.pet) {
    details.splice(2, 0, {
      label: "Pet",
      value: (
        <Chip
          icon={
            Boolean(appointement?.pet?.deceased) ? (
              <TbGrave2 />
            ) : (
              <i
                className={`fas ${appointement?.pet?.type?.icon}`}
                style={{
                  fontSize: 20,
                  color: "white",
                }}
              />
            )
          }
          label={appointement?.pet?.name}
          clickable
          component={Link}
          data-testid={"appointment.details.pet-chip"}
          to={petViewUrl(appointement?.pet?.id, appointement?.user?.id)}
          sx={{
            minWidth: 50,
          }}
        />
      ),
    });
  }

  return details;
}

function getAdminAppointmentDetails(appointement, service, t, currentClinic) {
  var userName = appointement?.user?.userDetails?.firstName;
  userName = appointement?.user?.userDetails?.lastName
    ? userName + " " + appointement?.user?.userDetails?.lastName
    : userName;

  const details = [
    {
      label: "DateOfAppointment",
      value:
        dayjs(getDateObject(appointement?.startDate)).format("DD/MM/YYYY") ||
        t(NO_INFO),
    },
    {
      label: "TimeOfAppointment",
      value:
        dayjs(getDateObject(appointement?.startDate)).format("HH:mm") +
          " - " +
          dayjs(getDateObject(appointement?.endDate)).format("HH:mm") ||
        t(NO_INFO),
    },
    {
      label: "Service",
      value: service || t(NO_INFO),
    },
  ];
  // Add location only if it's a different clinic.
  if (currentClinic && currentClinic?.id !== appointement?.clinic?.id) {
    details.push({
      label: "Location",
      value:
        `${appointement?.clinic?.title} - ${appointement?.clinic?.streetAddress}, ${appointement?.clinic?.city}` ||
        t(NO_INFO),
    });
  }

  if (
    appointement &&
    appointement?.user &&
    String(appointement?.user?.role) === USER
  ) {
    details.splice(2, 0, {
      label: "USER",
      value: (
        <Chip
          icon={
            appointement?.user?.businessAccount ? (
              <ApartmentIcon />
            ) : (
              <FaceIcon />
            )
          }
          label={userName}
          clickable
          component={Link}
          to={SINGLE_CLINIC_USER + appointement?.user?.id}
          data-testid={"appointment.details.user-chip"}
          sx={{
            minWidth: 50,
          }}
        />
      ),
    });
  } else if (appointement?.user?.clinic?.id == null) {
    details.splice(2, 0, {
      label: "USER",
      value: (
        <Chip
          icon={<FaceIcon />}
          label={t("DeletedUser")}
          sx={{
            minWidth: 50,
          }}
        />
      ),
    });
  }

  if (appointement && appointement?.pet) {
    details.splice(2, 0, {
      label: "Pet",
      value: (
        <Chip
          icon={
            Boolean(appointement?.pet?.deceased) ? (
              <TbGrave2 />
            ) : (
              <i
                className={`fas ${appointement?.pet?.type?.icon}`}
                style={{
                  fontSize: 20,
                  color: "white",
                }}
              />
            )
          }
          label={trimText(appointement?.pet?.name, 50)}
          clickable
          component={Link}
          data-testid={"appointment.details.pet-chip"}
          to={petViewUrl(appointement?.pet?.id, appointement?.user?.id)}
          sx={{
            minWidth: 50,
          }}
        />
      ),
    });
  }

  details.push(
    {
      label: "Description",
      value: appointement?.description || t(NO_INFO),
    },
    {
      label: "Treatment",
      value: appointement?.treatment?.description || t(NO_INFO),
    },
  );

  return details;
}
const useGetFinishAppointment = (appointement) => {
  const { roles } = useUser();
  const adminRole = roles.adminOrEmployee;
  const { t, i18n } = useTranslation();

  const currentLang = i18n.language;
  const [translations, setTranslations] = React.useState({
    species: "",
    service: "",
  });

  React.useEffect(() => {
    var currentLangName = null;
    var currentSpeciesName = null;
    if (appointement?.priceList?.translations != null) {
      currentLangName = appointement?.priceList?.translations[currentLang];
    }

    if (appointement?.pet?.type?.translations != null) {
      currentSpeciesName = appointement?.pet?.type?.translations[currentLang];
    }

    if (currentLangName != null && currentLangName != "") {
      setTranslations((prevState) => ({
        ...prevState,
        service: currentLangName,
      }));
    } else {
      setTranslations((prevState) => ({
        ...prevState,
        service: appointement?.priceList?.title,
      }));
    }

    if (currentSpeciesName != null && currentSpeciesName != "") {
      setTranslations((prevState) => ({
        ...prevState,
        species: currentSpeciesName,
      }));
    } else {
      setTranslations((prevState) => ({
        ...prevState,
        species: appointement?.pet?.type?.title,
      }));
    }
  }, [appointement, currentLang]);

  return getFinishAppointmentDetails(appointement, translations.service, t);
};

function getFinishAppointmentDetails(appointement, service, t) {
  var userName = appointement?.user?.userDetails?.firstName;
  userName = appointement?.user?.userDetails?.lastName
    ? userName + " " + appointement?.user?.userDetails?.lastName
    : userName;

  const details = [
    {
      label: "DateOfAppointment",
      value:
        dayjs(getDateObject(appointement?.startDate)).format("DD/MM/YYYY") ||
        t(NO_INFO),
    },
    {
      label: "TimeOfAppointment",
      value:
        dayjs(getDateObject(appointement?.startDate)).format("HH:mm") +
          " - " +
          dayjs(getDateObject(appointement?.endDate)).format("HH:mm") ||
        t(NO_INFO),
    },
    {
      label: "Service",
      value: service || t(NO_INFO),
    },
  ];

  if (
    appointement &&
    appointement?.user &&
    String(appointement?.user?.role) === USER
  ) {
    details.splice(2, 0, {
      label: "USER",
      value: userName,
    });
  }

  if (appointement && appointement?.pet) {
    details.splice(2, 0, {
      label: "Pet",
      value: trimText(appointement?.pet?.name, 50),
    });
  }

  details.push(
    {
      label: "Description",
      value: appointement?.description || t(NO_INFO),
    },
    {
      label: "Treatment",
      value: appointement?.treatment?.description || t(NO_INFO),
    },
  );

  return details;
}

export { useGetSingleAppointmentDetails, useGetFinishAppointment };
